import React, { useState } from "react";
import { getAuth } from "firebase/auth";
import { createTask } from "../../firebase/tasks";
import { useSession } from "../../firebase/UserProvider";
import { TaskTitleInput } from "./TaskTitleInput";
import { TaskInlineMetaBar } from "./TaskInlineMetaBar";
import { LabelTag } from "./LabelTag";
import { FiX, FiSend } from "react-icons/fi";

/**
 * HomeTaskCreateForm
 * Inline task creation form matching the functionality of the Add Task form on /tasks.
 */
export function HomeTaskCreateForm({
  users = [],
  allLabels = [],
  onClose,
  onSuccess,
}) {
  const { user } = useSession();
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [newPriority, setNewPriority] = useState("Low");
  const [newStatus, setNewStatus] = useState("Open");
  const [newAssigneeUid, setNewAssigneeUid] = useState("");
  const [newLabels, setNewLabels] = useState([]);
  const [newRecurrence, setNewRecurrence] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddLabel = (labelName) => {
    setNewLabels((prev) =>
      prev.includes(labelName) ? prev : [...prev, labelName]
    );
  };

  const handleRemoveLabel = (labelName) => {
    setNewLabels((prev) => prev.filter((l) => l !== labelName));
  };

  const handleReset = () => {
    setNewTitle("");
    setNewDescription("");
    setNewDueDate("");
    setNewPriority("Low");
    setNewStatus("Open");
    setNewAssigneeUid("");
    setNewLabels([]);
    setNewRecurrence(null);
  };

  const handleCancel = () => {
    handleReset();
    if (onClose) onClose();
  };

  const handleAddTask = async (e) => {
    if (e) e.preventDefault();
    if (!newTitle.trim() || !newAssigneeUid || isSubmitting) return;

    setIsSubmitting(true);
    const auth = getAuth();
    const currentUser = user || auth.currentUser;

    const cleanTitle = newTitle.replace(/@\S+/g, "").replace(/\s+/g, " ").trim();
    const newTaskData = {
      title: cleanTitle,
      description: newDescription.trim(),
      priority: newPriority,
      dueDate: newDueDate || null,
      status: newStatus,
      labels: newLabels,
      recurrence: newRecurrence || null,
      createdByUid: currentUser ? currentUser.uid : "anonymous",
      updatedByUid: currentUser ? currentUser.uid : "anonymous",
      assigneeUid: newAssigneeUid || null,
    };

    try {
      const created = await createTask(newTaskData);
      handleReset();
      if (onSuccess) onSuccess(created);
    } catch (err) {
      // Graceful error handling
    } finally {
      setIsSubmitting(false);
    }
  };

  const derivedAllLabels = Array.from(
    new Set(allLabels.map((l) => (l.name || "").toLowerCase()))
  ).map((name) => {
    const existing = allLabels.find((l) => (l.name || "").toLowerCase() === name);
    return existing || { id: name, name, color: "#6b7280" };
  });

  return (
    <form
      onSubmit={handleAddTask}
      className="border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm bg-white space-y-2 [&_button]:min-h-0"
    >
      <div className="space-y-1">
        <TaskTitleInput
          value={newTitle}
          onChange={setNewTitle}
          onAddLabel={handleAddLabel}
          allLabels={derivedAllLabels}
          autoFocus={true}
        />
        <textarea
          placeholder="Description"
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          className="w-full text-xs text-gray-500 placeholder-gray-400 border-none focus:ring-0 p-0 resize-none focus:outline-none mt-1 leading-relaxed"
          rows={2}
        />
      </div>

      <TaskInlineMetaBar
        newDueDate={newDueDate}
        onChangeDueDate={setNewDueDate}
        users={users}
        newAssigneeUid={newAssigneeUid}
        onChangeAssigneeUid={setNewAssigneeUid}
        newPriority={newPriority}
        onChangePriority={setNewPriority}
        newStatus={newStatus}
        onChangeStatus={setNewStatus}
        newRecurrence={newRecurrence}
        onChangeRecurrence={setNewRecurrence}
      />

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        {/* Labels */}
        <div className="flex flex-wrap items-center gap-1.5">
          {newLabels &&
            newLabels.length > 0 &&
            newLabels.map((labelName) => {
              const labelColor = derivedAllLabels.find(
                (l) => l.name.toLowerCase() === labelName.toLowerCase()
              )?.color;
              return (
                <LabelTag
                  key={labelName}
                  name={labelName}
                  color={labelColor}
                  onRemove={handleRemoveLabel}
                />
              );
            })}
        </div>

        <div className="flex items-center gap-2 ml-auto shrink-0">
          <div className="relative group/cancel">
            <button
              type="button"
              onClick={handleCancel}
              className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-150 border-none outline-none focus:outline-none cursor-pointer"
              aria-label="Cancel"
            >
              <FiX className="w-4 h-4" />
            </button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/cancel:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
              Cancel
            </span>
          </div>

          <div className="relative group/save">
            <button
              type="submit"
              disabled={!newTitle.trim() || !newAssigneeUid || isSubmitting}
              className="w-8 h-8 flex items-center justify-center text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed border-none outline-none focus:outline-none cursor-pointer"
              aria-label="Save"
            >
              <FiSend className="w-4 h-4" />
            </button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/save:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
              Save
            </span>
          </div>
        </div>
      </div>
    </form>
  );
}
